import {Intervals} from "../../Intervals"

/**
 * Excursion Report settings schema.
 *
 * Reference implementation for the schema-driven report settings pattern
 * (ENGS-677). Setting keys are unchanged from the ExcursionReportSettings.vue
 * component this replaces, so saved templates and generated PDFs are unaffected.
 */

// The logging periods can only be edited when the report is not on the
// "day's data" interval, which has no configurable windows.
const loggingPeriodsEditable = ({settings, interval}) => interval !== Intervals.DAILY_DATA

const timeOrDash = (value) => (value === null || value === undefined || value === '' ? '--' : value)

export default {
    slug: 'excursion-report',
    icon:
        '<path stroke="none" d="M0 0h24v24H0z" fill="none"/>'
        + '<path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />'
        + '<path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />'
        + '<path d="M12 11v4" />'
        + '<path d="M12 18v.01" />',
    permission: 'manage_excursion_report',
    title: 'Excursion Report',
    description: 'Evidence that each point stayed within its limits, and a record of when it did not.',
    info: 'For each selected point: a Data Summary table with two logged readings per day, an optional graph, and every alarm in the period with its timing and comments. Use it as a record of whether each point stayed within its alarm limits.',

    sections: [
        {
            title: 'Data Settings',
            description: 'Choose what data appears in the generated report.',
            fields: [
                {
                    key: 'includeMinMax',
                    type: 'toggle',
                    label: 'Min/Max Values',
                    description: 'Adds Max and Min rows with the highest and lowest reading for each day, or each reading window, and marks them on the graph.',
                },
                {
                    key: 'includeDewPoint',
                    type: 'toggle',
                    label: 'Dew Point',
                    description: 'Adds a dew point row for each logged reading. Only filled in for a point whose probe also measures the matching temperature or humidity; otherwise the cells are blank.',
                },
                {
                    key: 'includeMKT',
                    type: 'toggle',
                    label: 'Mean Kinetic Temperature (MKT)',
                    description: 'Adds a Mean Kinetic Temperature row for each day, or each reading window, calculated from every reading in that span. Temperature points only.',
                    // The full name is too long for the card's at-a-glance chip.
                    chipLabel: 'MKT',
                },
                {
                    key: 'includeTimeOutOfRange',
                    type: 'toggle',
                    label: 'Time Out of Range',
                    description: 'Adds a row with the minutes each day, or each reading window, that readings were above the high alarm limit or below the low alarm limit. Caution limits are not counted.',
                },
                {
                    key: 'includeData',
                    type: 'toggle',
                    label: 'All Readings',
                    description: 'Adds a Readings section after the alarm details listing every reading in the period. Rows past an alarm limit are red; rows past a caution limit are yellow.',
                },
                {
                    key: 'includeAlarmExcursionGraph',
                    type: 'toggle',
                    label: 'Enhanced Excursion Alarm Details',
                    description: 'For each alarm (except offline and battery alarms), adds a graph from shortly before the alarm to shortly after it resolved, with the average, min, max and time spent in each range.',
                    chipLabel: 'Alarm Details',
                },
                {
                    key: 'includeShowMeVax',
                    type: 'toggle',
                    label: 'ShowMeVax CSV Generation',
                    chipLabel: 'ShowMeVax CSV',
                    description: 'Attaches a ShowMeVax-format CSV to the report email for each temperature point, with date, time, reading, min and max in Celsius.',
                    // `showShowMeVax` is a company-level availability flag stored
                    // alongside the settings, not a user-facing setting.
                    showIf: ({settings}) => settings.showShowMeVax === true,
                },
                {
                    key: 'hideGraph',
                    type: 'toggle',
                    label: 'Hide Graph',
                    description: 'Removes the readings graph from each point\'s Data Summary page. The summary table and alarm details still appear.',
                    // Turning this on removes content, so it must not appear in
                    // the card's "what's enabled" summary as if it added a
                    // feature. See enabledFeatures.js.
                    summarizeAs: 'omission',
                    showIf: ({settings}) => settings.showHideGraph === true,
                },
            ],
        },

        {
            title: 'Daily Readings',
            description: 'The report records a reading twice a day. Set the two times below.',
            showIf: loggingPeriodsEditable,
            // The two reading times sit side by side on the card layout.
            columns: 2,
            fields: [
                {
                    key: 'reportStyle',
                    type: 'select',
                    label: 'Summarize Values',
                    description: 'Per Calendar Day summarizes each day from midnight to midnight. Per Reading Window gives two tables per day: the previous day\'s second reading time to the first, then the first to the second.',
                    segmented: true,
                    fullWidth: true,
                    options: {
                        ExcursionSplitLog: 'Per Reading Window',
                        ExcursionDontSplitLog: 'Per Calendar Day',
                    },
                },
                {
                    key: 'logTime1',
                    type: 'time',
                    label: 'First reading',
                    description: 'Each day the report shows the last reading recorded between midnight and this time, in the site\'s time zone. With Per Reading Window, this is also where the two windows meet.',
                    // The reading window this time starts.
                    hint: (settings) => `${timeOrDash(settings.logTime1)} to ${timeOrDash(settings.logTime2)}`,
                },
                {
                    key: 'logTime2',
                    type: 'time',
                    label: 'Second reading',
                    description: 'Each day the report shows the last reading recorded after the first reading time and up to this time. With Per Reading Window, this is also where the two windows meet.',
                    hint: (settings) => `${timeOrDash(settings.logTime2)} to ${timeOrDash(settings.logTime1)}`,
                },
                {
                    // Only meaningful when the high/low is split by period; with
                    // "the whole day" the report ignores these windows entirely,
                    // so showing them would imply they still apply. The card
                    // layout shows each window beside its time instead.
                    key: 'loggingPeriod1',
                    type: 'static',
                    label: 'Period 1',
                    description: 'Runs from the second reading time on the previous day to the first reading time today. The first table\'s min, max, MKT and time out of range use this span.',
                    showIf: ({settings, variant}) => variant !== 'cards' && settings.reportStyle === 'ExcursionSplitLog',
                    value: (settings) => `${timeOrDash(settings.logTime2)} - ${timeOrDash(settings.logTime1)}`,
                },
                {
                    key: 'loggingPeriod2',
                    type: 'static',
                    label: 'Period 2',
                    description: 'Runs from the first reading time to the second reading time on the same day. The second table\'s min, max, MKT and time out of range use this span.',
                    showIf: ({settings, variant}) => variant !== 'cards' && settings.reportStyle === 'ExcursionSplitLog',
                    value: (settings) => `${timeOrDash(settings.logTime1)} - ${timeOrDash(settings.logTime2)}`,
                },
            ],
        },

        {
            // Only meaningful on a saved template: this is the recurring send
            // schedule. On-demand runs pick an explicit range on the run form
            // instead, so this section is hidden there.
            title: 'Scheduled Delivery',
            description: 'Automatically send this report on a recurring schedule.',
            showIf: ({isSavable}) => isSavable === true,
            fields: [
                {
                    key: 'interval',
                    type: 'select',
                    label: 'Send frequency',
                    description: 'How often the report is emailed, around 1 AM site time. Monthly sends on the 1st; semi-monthly on the 1st and 16th.',
                    options: Intervals.INTERVAL_OPTIONS,
                    fromContext: true,
                },
                {
                    key: 'weekday',
                    type: 'select',
                    label: 'Day to receive report',
                    description: 'The day the weekly report is emailed, around 1 AM site time.',
                    options: Intervals.WEEKDAY_OPTIONS,
                    showIf: ({interval}) => interval === Intervals.WEEKLY,
                },
            ],
        },
    ],
}
